/*
  GDrivePickerDialog — clinic-wide Google Drive folder + DOCX picker.

  Flow:
    1. Check /gdrive/status
    2. If not connected → show "Connect Drive" button that opens Google
       OAuth in a popup. When the popup redirects back to `/?gdrive_connected=1`,
       the parent window polls status until connected, then closes the popup.
    3. Once connected → browse folders (root ↓ children), pick a folder,
       multi-select .docx files, click Import.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2, Folder, FileText, ChevronRight, ChevronLeft, RefreshCw, Link2, Unlink,
  Cloud, CheckCircle2, Bug,
} from "lucide-react";

export default function GDrivePickerDialog({ open, onOpenChange, email, canonicalClientId, onImported }) {
  const [status, setStatus] = useState(null); // { connected, ... }
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const [path, setPath] = useState([]); // breadcrumb stack of {id, name}
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [importing, setImporting] = useState(false);
  // Configured root for the "programs" scope — when set, the picker
  // starts here and staff cannot go above it.
  const [scopeRoot, setScopeRoot] = useState(null); // {folder_id, folder_name}

  const [diagOpen, setDiagOpen] = useState(false);
  const [diag, setDiag] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);

  const pollerRef = useRef(null);
  const popupRef = useRef(null);

  const loadDiagnostics = useCallback(async () => {
    setLoadingDiag(true);
    try {
      const r = await api.get("/exercise-programs/gdrive/diagnostics", { params: { limit: 5 } });
      setDiag(r.data);
    } catch (e) {
      toast.error("Couldn't load diagnostics", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoadingDiag(false);
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    setCheckingStatus(true);
    try {
      const r = await api.get("/exercise-programs/gdrive/status");
      setStatus(r.data || { connected: false });
      setScopeRoot(r.data?.programs_root || null);
      return r.data;
    } catch (e) {
      setStatus({ connected: false });
    } finally {
      setCheckingStatus(false);
    }
  }, []);

  const loadFolder = useCallback(async (parentId) => {
    setLoadingList(true);
    setSelectedFileIds(new Set());
    try {
      // Pass scope=programs so backend defaults parent to the
      // configured root when parentId is null, and rejects any
      // parent outside the root.
      const params = parentId
        ? { parent_id: parentId, scope: "programs" }
        : { scope: "programs" };
      const [foldersR] = await Promise.all([
        api.get("/exercise-programs/gdrive/folders", { params }),
      ]);
      // The server tells us which folder it actually listed (either
      // the requested parentId, or the scope root when parentId is null).
      const listedFolderId = foldersR.data?.parent_id || parentId || "root";
      const filesR = await api.get("/exercise-programs/gdrive/files", {
        params: {
          folder_id: listedFolderId,
          include_spreadsheets: true,
          scope: "programs",
        },
      });
      setFolders(foldersR.data?.folders || []);
      setFiles(filesR.data?.files || []);
    } catch (e) {
      toast.error("Couldn't load Drive", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    refreshStatus().then((s) => {
      if (s?.connected) {
        const root = s?.programs_root;
        setPath(root ? [{ id: root.folder_id, name: root.folder_name }] : []);
        loadFolder(root ? root.folder_id : null);
      }
    });
    // Also listen for the popup's postMessage — this is the most
    // reliable signal that the OAuth flow finished, and is delivered
    // the moment the callback page mounts inside the popup.
    async function onMessage(ev) {
      if (ev?.data?.type !== "gdrive_oauth") return;
      if (ev.data.error) {
        toast.error("Google Drive connect failed", {
          description: decodeURIComponent(ev.data.error).slice(0, 240),
        });
        // Auto-open diagnostics so the operator sees the raw scopes
        // Google returned without having to hunt for the panel.
        setDiagOpen(true);
        loadDiagnostics();
        return;
      }
      if (ev.data.ok) {
        if (pollerRef.current) { clearInterval(pollerRef.current); pollerRef.current = null; }
        const s = await refreshStatus();
        if (s?.connected) {
          const root = s?.programs_root;
          setPath(root ? [{ id: root.folder_id, name: root.folder_name }] : []);
          await loadFolder(root ? root.folder_id : null);
          toast.success(
            s.account_email ? `Connected as ${s.account_email}` : "Google Drive connected",
          );
        }
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (pollerRef.current) clearInterval(pollerRef.current);
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    };
  }, [open, refreshStatus, loadFolder]);

  async function startConnect() {
    setConnecting(true);
    const before = {
      email: status?.account_email || null,
      updatedAt: status?.updated_at || null,
    };
    try {
      const r = await api.get("/exercise-programs/gdrive/connect");
      const authUrl = r.data?.authorization_url;
      if (!authUrl) throw new Error("No authorization URL returned");
      const w = window.open(authUrl, "gdrive-oauth", "width=520,height=640");
      popupRef.current = w;
      // Poll status every 1.5s. Keep polling for up to ~15s AFTER the
      // popup closes so a slow callback (Google + backend token
      // exchange) still gets a chance to be detected.
      let popupClosedAt = 0;
      pollerRef.current = setInterval(async () => {
        const s = await refreshStatus();
        const changed =
          !!s?.connected &&
          (
            !before.updatedAt ||
            (s.updated_at && s.updated_at !== before.updatedAt) ||
            (s.account_email && s.account_email !== before.email)
          );
        if (changed) {
          clearInterval(pollerRef.current);
          pollerRef.current = null;
          if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
          const root = s?.programs_root;
          setPath(root ? [{ id: root.folder_id, name: root.folder_name }] : []);
          await loadFolder(root ? root.folder_id : null);
          toast.success(
            before.email && s.account_email && s.account_email !== before.email
              ? `Switched to ${s.account_email}`
              : s.account_email ? `Connected as ${s.account_email}` : "Google Drive connected",
          );
        } else if (popupRef.current && popupRef.current.closed) {
          if (!popupClosedAt) popupClosedAt = Date.now();
          if (Date.now() - popupClosedAt > 15000) {
            clearInterval(pollerRef.current);
            pollerRef.current = null;
          }
        }
      }, 1500);
    } catch (e) {
      toast.error("Couldn't start Drive connection", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setConnecting(false);
    }
  }

  async function disconnect() {
    if (!confirm("Disconnect Google Drive from the clinic? Users will need to reconnect.")) return;
    try {
      await api.post("/exercise-programs/gdrive/disconnect");
      setStatus({ connected: false });
      setFolders([]); setFiles([]); setPath([]); setSelectedFileIds(new Set());
      toast.success("Google Drive disconnected");
    } catch (e) {
      toast.error("Disconnect failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  function openFolder(f) {
    setPath((p) => [...p, { id: f.id, name: f.name }]);
    loadFolder(f.id);
  }

  function goBack() {
    setPath((p) => {
      const minLen = scopeRoot ? 1 : 0;
      if (p.length <= minLen) return p;
      const next = p.slice(0, -1);
      const parent = next.length > 0 ? next[next.length - 1].id : null;
      loadFolder(parent);
      return next;
    });
  }

  function toggleFile(id) {
    setSelectedFileIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelectedFileIds((s) => {
      if (s.size === files.length) return new Set();
      return new Set(files.map((f) => f.id));
    });
  }

  async function bulkImport() {
    if (selectedFileIds.size === 0 || !email) return;
    setImporting(true);
    try {
      const enc = encodeURIComponent(email);
      const r = await api.post(
        `/health-record/${enc}/exercise-programs/gdrive/import`,
        {
          file_ids: Array.from(selectedFileIds),
          service_type: "exercise_physiology",
          tags: [],
          auto_publish: true,
          // Scope the new programs to the specific sibling when the
          // workspace is at ``/clients/<email>?as=<cid>`` — otherwise
          // Judy's imports land invisibly under Rosemarie's cid or
          // stay unstamped and never show in Judy's Programs tab.
          canonical_client_id: canonicalClientId || null,
        },
      );
      const { imported, failed, results } = r.data || {};
      if (failed > 0) {
        toast.warning(`Imported ${imported}, ${failed} failed`, {
          description: (results || [])
            .filter((x) => !x.ok)
            .slice(0, 3)
            .map((x) => x.error)
            .join(" · "),
        });
      } else {
        toast.success(`Imported ${imported} program(s) as drafts`);
      }
      setSelectedFileIds(new Set());
      onImported?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Bulk import failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setImporting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl w-[92vw] max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="gdrive-picker-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="h-4 w-4 text-indigo-700" />
            Import from Google Drive
            {status?.connected && (
              <span
                className="ml-1 text-2xs font-normal text-emerald-700 inline-flex items-center gap-1"
                data-testid="gdrive-connected-badge"
              >
                <CheckCircle2 className="h-3 w-3" /> connected
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Clinic-wide connection. Selected files (.docx, .xlsx, Google Sheets)
            are imported as drafts, ready for you to review and activate.
          </DialogDescription>
          {status?.connected && (
            <div className="mt-1 flex items-center gap-2 text-2xs text-zinc-600"
                 data-testid="gdrive-connected-account">
              <span>Connected as</span>
              <span className="font-medium text-zinc-800 truncate max-w-[240px]"
                    title={status.account_email || "unknown account"}>
                {status.account_email || <span className="italic text-zinc-500">unknown account</span>}
              </span>
              <span className="text-zinc-300">·</span>
              <button
                onClick={startConnect}
                disabled={connecting}
                className="text-indigo-700 hover:underline inline-flex items-center gap-1"
                data-testid="gdrive-switch-account-btn"
                title="Sign in with a different Google account"
              >
                {connecting
                  ? <><Loader2 className="h-3 w-3 animate-spin" /> Opening…</>
                  : <>Switch account</>}
              </button>
            </div>
          )}
        </DialogHeader>

        {checkingStatus && !status ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Checking Drive status…
          </div>
        ) : !status?.connected ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 py-10 text-center">
            <Cloud className="h-8 w-8 text-zinc-400" />
            <div className="text-sm text-zinc-700 max-w-sm">
              Google Drive isn&apos;t connected yet. Connect your clinic&apos;s Drive to
              browse folders and bulk-import programs.
            </div>
            <Button onClick={startConnect} disabled={connecting} data-testid="gdrive-connect-btn">
              {connecting
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Opening Google…</>
                : <><Link2 className="h-3.5 w-3.5 mr-1" /> Connect Google Drive</>}
            </Button>
            <button
              type="button"
              onClick={() => { setDiagOpen((v) => !v); if (!diag) loadDiagnostics(); }}
              className="text-2xs text-zinc-500 hover:text-zinc-700 underline inline-flex items-center gap-1 mt-2"
              data-testid="gdrive-diagnostics-toggle"
            >
              <Bug className="h-3 w-3" />
              {diagOpen ? "Hide diagnostics" : "View last connection attempt"}
            </button>
            {diagOpen && (
              <div className="w-full max-w-xl text-left border border-zinc-200 rounded-md p-3 bg-zinc-50 text-2xs font-mono text-zinc-700 overflow-x-auto"
                   data-testid="gdrive-diagnostics-panel">
                {loadingDiag ? (
                  <div className="text-zinc-500">Loading…</div>
                ) : !diag ? (
                  <div className="text-zinc-500">No data yet.</div>
                ) : (
                  <>
                    <div className="mb-2 pb-2 border-b border-zinc-200">
                      <div className="font-semibold text-zinc-800 mb-1">OAuth config</div>
                      <div>redirect_uri: <span className="text-indigo-700 break-all">{diag.config?.redirect_uri}</span></div>
                      <div>client_id: …{diag.config?.client_id_tail}</div>
                      <div>requested_scopes: {(diag.config?.requested_scopes || []).join(", ") || "—"}</div>
                      <div>acceptable_scopes: {(diag.config?.acceptable_scopes || []).join(", ") || "—"}</div>
                    </div>
                    <div className="font-semibold text-zinc-800 mb-1">
                      Recent attempts ({diag.attempts?.length || 0})
                    </div>
                    {(diag.attempts || []).length === 0 && (
                      <div className="text-zinc-500">No attempts recorded yet.</div>
                    )}
                    {(diag.attempts || []).map((a, i) => (
                      <div key={i} className="mb-2 pb-2 border-b border-zinc-200 last:border-b-0 last:mb-0 last:pb-0">
                        <div className={a.status === "success" ? "text-emerald-700" : "text-red-700"}>
                          [{a.at}] {a.status}{a.stage ? ` @ ${a.stage}` : ""}
                        </div>
                        {a.error && <div>error: {a.error}</div>}
                        {a.error_detail && <div className="whitespace-pre-wrap break-all">detail: {a.error_detail}</div>}
                        {a.requested_scopes && <div>requested: {a.requested_scopes.join(", ")}</div>}
                        {a.granted_scopes && <div>granted: {a.granted_scopes.join(", ") || "none"}</div>}
                        {a.raw_token_scope_field !== undefined && a.raw_token_scope_field !== null && (
                          <div className="text-amber-800">raw scope field: {a.raw_token_scope_field || <em>empty string</em>}</div>
                        )}
                        {a.raw_token_fields && (
                          <div>raw token fields: {a.raw_token_fields.join(", ")}</div>
                        )}
                        {a.google_scope_param && <div>google_scope_param: {a.google_scope_param}</div>}
                        {a.authuser !== undefined && a.authuser !== null && <div>authuser: {a.authuser}</div>}
                        {a.prompt_param && <div>prompt_param: {a.prompt_param}</div>}
                        {a.account_email && <div>account_email: {a.account_email}</div>}
                        {a.has_refresh_token !== undefined && (
                          <div>refresh_token: {a.has_refresh_token ? "yes" : "no"} · id_token: {a.has_id_token ? "yes" : "no"}</div>
                        )}
                      </div>
                    ))}
                  </>
                )}
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="ghost" onClick={loadDiagnostics} disabled={loadingDiag}
                          data-testid="gdrive-diagnostics-refresh">
                    <RefreshCw className="h-3 w-3 mr-1" /> Refresh
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {scopeRoot && (
              <div
                className="flex items-center gap-2 px-2 py-1.5 mt-2 text-2xs rounded bg-indigo-50 border border-indigo-100 text-indigo-800"
                data-testid="gdrive-scope-banner"
              >
                <Cloud className="h-3 w-3" />
                <span>
                  Imports scoped to <span className="font-medium">{scopeRoot.folder_name}</span>
                  {" "}— folders outside this are hidden.
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 py-2 border-b border-zinc-200 text-xs text-zinc-600">
              <Button size="sm" variant="ghost" onClick={goBack} disabled={path.length <= (scopeRoot ? 1 : 0)}>
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </Button>
              <span className="flex items-center gap-1 flex-1 min-w-0 truncate">
                <span className="text-zinc-500">
                  {scopeRoot ? scopeRoot.folder_name : "My Drive"}
                </span>
                {path.map((p, idx) => {
                  // When scoped, the first path entry IS the scope
                  // root — the folder-name label already shows it,
                  // so skip it to avoid "Root / Root / …".
                  if (scopeRoot && idx === 0) return null;
                  return (
                    <span key={p.id} className="flex items-center gap-1">
                      <ChevronRight className="h-3 w-3 text-zinc-400" />
                      <span className="truncate">{p.name}</span>
                    </span>
                  );
                })}
              </span>
              <Button size="sm" variant="ghost" onClick={() => loadFolder(path[path.length - 1]?.id || null)}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm" variant="ghost"
                onClick={disconnect}
                title="Disconnect Drive"
                className="text-red-600 hover:text-red-700"
                data-testid="gdrive-disconnect-btn"
              >
                <Unlink className="h-3.5 w-3.5 mr-1" /> Disconnect
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto py-2 space-y-1">
              {loadingList ? (
                <div className="text-sm text-zinc-500 py-6 text-center">
                  <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
                </div>
              ) : (
                <>
                  {folders.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => openFolder(f)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 text-left text-sm"
                      data-testid={`gdrive-folder-${f.id}`}
                    >
                      <Folder className="h-4 w-4 text-amber-600" />
                      <span className="flex-1 truncate">{f.name}</span>
                      <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
                    </button>
                  ))}

                  {files.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-zinc-100">
                      <div className="flex items-center justify-between px-2 py-1 text-2xs uppercase tracking-wide text-zinc-500">
                        <span>Program files ({files.length})</span>
                        <button
                          onClick={toggleAll}
                          className="text-indigo-700 hover:underline normal-case font-normal"
                          data-testid="gdrive-select-all"
                        >
                          {selectedFileIds.size === files.length ? "Clear all" : "Select all"}
                        </button>
                      </div>
                      {files.map((f) => (
                        <label
                          key={f.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-100 text-sm cursor-pointer"
                          data-testid={`gdrive-file-${f.id}`}
                        >
                          <Checkbox
                            checked={selectedFileIds.has(f.id)}
                            onCheckedChange={() => toggleFile(f.id)}
                          />
                          <FileText className="h-4 w-4 text-zinc-500" />
                          <span className="flex-1 truncate">{f.name}</span>
                          {f.size && (
                            <span className="text-2xs text-zinc-400">
                              {(parseInt(f.size, 10) / 1024).toFixed(0)} KB
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}

                  {folders.length === 0 && files.length === 0 && (
                    <div className="text-sm text-zinc-500 py-6 text-center">
                      {path.length === 0
                        ? "No folders in your Drive root."
                        : "This folder has no sub-folders or program files (.docx / .xlsx / Google Sheets)."}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        <DialogFooter className="border-t pt-3">
          <div className="flex items-center gap-2 w-full">
            <span className="text-xs text-zinc-500 flex-1">
              {selectedFileIds.size > 0 && `${selectedFileIds.size} file(s) selected`}
            </span>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={bulkImport}
              disabled={importing || selectedFileIds.size === 0 || !status?.connected}
              data-testid="gdrive-import-btn"
            >
              {importing
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Importing…</>
                : <>Import {selectedFileIds.size || ""} as drafts</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
